import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  VStack,
  HStack,
  Spinner,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  useDisclosure,
  useBreakpointValue,
} from '@chakra-ui/react';
import { ArrowBackIcon, ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import successImage from '../../src/Assets/icons/success.png';
import { useNavigate, useLocation } from 'react-router-dom';

import { useResetPasswordWithOtp } from '../utils/auth.api';

export default function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { mutateAsync: resetPasswordWithOtp, isLoading } = useResetPasswordWithOtp();
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isNewPasswordValid, setIsNewPasswordValid] = useState(false);
  const [isConfirmPasswordValid, setIsConfirmPasswordValid] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    hasCapital: false,
    hasNumber: false,
    hasSpecial: false
  });
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  // Get email and OTP from navigation state
  const email = location.state?.email || '';
  const otp = location.state?.otp || '';

  // Password validation functions
  const validatePassword = (password) => {
    const validation = {
      length: password.length >= 8,
      hasCapital: /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    };
    return validation;
  };

  const isPasswordValid = (validation) => {
    return validation.length && validation.hasCapital && validation.hasNumber && validation.hasSpecial;
  };

  const handleNewPasswordChange = (e) => {
    const password = e.target.value;
    setNewPassword(password);
    
    const validation = validatePassword(password);
    setPasswordValidation(validation);
    setIsNewPasswordValid(isPasswordValid(validation));
    
    // Also check confirm password validity when new password changes
    if (confirmPassword) {
      setIsConfirmPasswordValid(password === confirmPassword);
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const password = e.target.value;
    setConfirmPassword(password);
    setIsConfirmPasswordValid(password === newPassword && newPassword !== '');
  };

  // const handleSubmit = async e => {
  //   e.preventDefault();

  //   if (!newPassword || !confirmPassword) {
  //     alert('Please fill in both password fields');
  //     return;
  //   }

  //   if (newPassword.length < 8) {
  //     alert('Password must be at least 8 characters');
  //     return;
  //   }

  //   if (newPassword !== confirmPassword) {
  //     alert('Passwords do not match');
  //     return;
  //   }

  //   if (!email || !otp) {
  //     alert('Missing email or OTP. Please try again from the beginning.');
  //     return;
  //   }

  //   try {
  //     const payload = {
  //       email: email,
  //       otp: otp,
  //       new_password: newPassword
  //     };

  //     await resetPasswordWithOtp(payload);
      
  //     // Show success modal when API returns success
  //     onOpen();
  //   } catch (error) {
  //     console.error('Password reset failed:', error);
  //     alert('Password reset failed. Please try again.');
  //   }
  // };

  const handleSubmit = async e => {
    e.preventDefault();
  
    if (!newPassword || !confirmPassword) {
      alert('Please fill in both password fields');
      return;
    }
  
    if (newPassword.length < 8) {
      alert('Password must be at least 8 characters');
      return;
    }
  
    if (newPassword !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }
  
    if (!email || !otp) {
      alert('Missing email or OTP. Please try again from the beginning.');
      return;
    }
  
    try {
      const payload = {
        email: email,
        otp: otp,
        new_password: newPassword,
      };
  
             // Await API call and get response
       const response = await resetPasswordWithOtp(payload);

       console.log("reset password response..", response)
  
      // ✅ Check response message
      if (response?.message === 'Password reset successful') {
        onOpen(); // open success modal
      } else if (response?.detail) {
        alert(response.detail); // e.g. "OTP not found or expired"
      } else {
        alert('Unexpected response from server');
      }
    } catch (error) {
      console.error('Password reset failed:', error);
      alert(error.message || 'Password reset failed. Please try again.');
    }
  };
  
  const handleVerifyAccount = () => {
    // Close modal and navigate to login page
    onClose();
    navigate('/sign-in');
  };

  return (
    <>
      <Box minH="100vh" bg="white">
        <Container maxW="sm" centerContent>
          <VStack spacing={8} w="100%" pt={8}>
            {/* Header with back button */}
            <HStack w="100%" justify="space-between" align="center">
              <IconButton
                icon={<ArrowBackIcon />}
                variant="ghost"
                onClick={() => navigate(-1)}
                aria-label="Go back"
              />
              <Text fontWeight="semibold" fontSize="lg">
                Reset Password
              </Text>
              <Box w="40px" /> {/* Spacer for centering */}
            </HStack>

            {/* Title Section */}
            <Box textAlign="left" w="100%">
              <Heading
                as="h1"
                size="lg"
                color="gray.900"
                fontWeight="bold"
                lineHeight="1.2"
                mb={3}
              >
                Reset Password
              </Heading>
              <Text color="gray.500" fontSize="sm" lineHeight="1.4">
                Your new password must 
                be different from the previously used
                password
              </Text>
            </Box>

            {/* Form Section */}
            <VStack as="form" spacing={6} w="100%" onSubmit={handleSubmit}>
              <FormControl>
                <FormLabel color="gray.700" fontWeight="medium">
                  New Password
                </FormLabel>
                <InputGroup>
                  <Input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={handleNewPasswordChange}
                    placeholder=""
                    size="lg"
                    bg="gray.50"
                    borderColor={
                      newPassword === '' 
                        ? 'gray.200' 
                        : isNewPasswordValid 
                          ? 'green.400' 
                          : 'red.400'
                    }
                    borderWidth={newPassword === '' ? '1px' : '2px'}
                    borderRadius="lg"
                    _placeholder={{ color: 'gray.600' }}
                    _focus={{
                      borderColor: isNewPasswordValid ? 'green.400' : 'red.400',
                      borderWidth: '2px',
                      boxShadow: isNewPasswordValid 
                        ? '0 0 0 1px rgba(72, 187, 120, 0.3)' 
                        : '0 0 0 1px rgba(245, 101, 101, 0.3)'
                    }}
                    isDisabled={isLoading}
                  />
                  <InputRightElement w="3rem" h="100%">
                    <IconButton
                      variant="ghost"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      icon={
                        showNewPassword ? (
                              <ViewIcon fontSize="lg" />
                   
                        ) : (
                             <ViewOffIcon fontSize="lg" />
                        )
                      }
                      size="sm"
                      aria-label={
                        showNewPassword ? 'Hide password' : 'Show password'
                      }
                      minW="auto"
                      h="auto"
                    />
                  </InputRightElement>
                </InputGroup>
                <VStack spacing={1} align="start" mt={2}>
                  <Text fontSize="xs" color="gray.600" fontWeight="medium">
                    Password must contain:
                  </Text>
                  <VStack spacing={0.5} align="start" ml={2}>
                    <Text 
                      fontSize="xs" 
                      color={passwordValidation.length ? 'green.500' : 'red.500'}
                      display="flex"
                      alignItems="center"
                    >
                      {passwordValidation.length ? '✓' : '✗'} At least 8 characters
                    </Text>
                    <Text 
                      fontSize="xs" 
                      color={passwordValidation.hasCapital ? 'green.500' : 'red.500'}
                      display="flex"
                      alignItems="center"
                    >
                      {passwordValidation.hasCapital ? '✓' : '✗'} At least One capital letter
                    </Text>
                    <Text 
                      fontSize="xs" 
                      color={passwordValidation.hasNumber ? 'green.500' : 'red.500'}
                      display="flex"
                      alignItems="center"
                    >
                      {passwordValidation.hasNumber ? '✓' : '✗'} At leastOne number
                    </Text>
                    <Text 
                      fontSize="xs" 
                      color={passwordValidation.hasSpecial ? 'green.500' : 'red.500'}
                      display="flex"
                      alignItems="center"
                    >
                      {passwordValidation.hasSpecial ? '✓' : '✗'} At least One special character
                    </Text>
                  </VStack>
                </VStack>
              </FormControl>

              <FormControl>
                <FormLabel color="gray.700" fontWeight="medium">
                  Confirm Password
                </FormLabel>
                <InputGroup>
                  <Input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    placeholder=""
                    size="lg"
                    bg="gray.50"
                    borderColor={
                      confirmPassword === '' 
                        ? 'gray.200' 
                        : isConfirmPasswordValid 
                          ? 'green.400' 
                          : 'red.400'
                    }
                    borderWidth={confirmPassword === '' ? '1px' : '2px'}
                    borderRadius="lg"
                    _placeholder={{ color: 'gray.600' }}
                    _focus={{
                      borderColor: isConfirmPasswordValid ? 'green.400' : 'red.400',
                      borderWidth: '2px',
                      boxShadow: isConfirmPasswordValid 
                        ? '0 0 0 1px rgba(72, 187, 120, 0.3)' 
                        : '0 0 0 1px rgba(245, 101, 101, 0.3)'
                    }}
                    isDisabled={isLoading}
                  />
                  <InputRightElement w="3rem" h="100%">
                    <IconButton
                      variant="ghost"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      icon={
                        showConfirmPassword ? (
                            <ViewIcon fontSize="lg" />
                        ) : (
                     
                             <ViewOffIcon fontSize="lg" />
                        )
                      }
                      size="sm"
                      aria-label={
                        showConfirmPassword ? 'Hide password' : 'Show password'
                      }
                      minW="auto"
                      h="auto"
                    />
                  </InputRightElement>
                </InputGroup>
                <Text 
                  fontSize="xs" 
                  color={confirmPassword === '' ? 'gray.500' : isConfirmPasswordValid ? 'green.500' : 'red.500'} 
                  mt={1}
                  display="flex"
                  alignItems="center"
                >
                  {confirmPassword === '' ? '•' : isConfirmPasswordValid ? '✓' : '✗'} Passwords must match with new password
                </Text>
              </FormControl>

              {/* Reset Password Button */}
              <Button
                type="submit"
                w="100%"
                bg="#5A5BED"
                height="50px"
                color="white"
                fontWeight="semibold"
                borderRadius="100px"
                isLoading={isLoading}
                loadingText="Resetting..."
                spinner={<Spinner size="sm" color="white" />}
                _hover={{ bg: '#4A4BDD' }}
                mt={{ base: 16, md: 8 }}
                isDisabled={
                  !newPassword || !confirmPassword || !isNewPasswordValid || !isConfirmPasswordValid
                }
              >
                Reset Password
              </Button>
            </VStack>
          </VStack>
        </Container>
      </Box>

      {/* Success Modal - Conditional rendering based on screen size */}
      {isMobile ? (
        // Mobile Success Modal - Bottom Sheet
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          size="full"
          motionPreset="slideInBottom"
          closeOnOverlayClick={true}
        >
          <ModalOverlay
            bg="rgba(0, 0, 0, 0.4)"
            backdropFilter="blur(8px)"
            sx={{
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
            }}
          />
          <ModalContent
            bg="white"
            borderTopRadius="24px"
            borderBottomRadius="0"
            position="absolute"
            bottom="0"
            left="0"
            right="0"
            top="auto"
            height="auto"
            minHeight="45vh"
            maxHeight="60vh"
            margin="0"
            marginTop="auto"
            transform="none !important"
            style={{ transform: 'none' }}
            boxShadow="0 -10px 25px rgba(0,0,0,0.15)"
          >
            <Box px={6} py={8}>
              {/* Modal handle/indicator */}
              <Box
                w="50px"
                h="5px"
                bg="gray.300"
                borderRadius="full"
                mx="auto"
                mb={8}
              />

              {/* Background decorative elements */}
              <Box position="absolute" top="20%" left="10%" w="16px" h="16px">
                <Text fontSize="lg" color="red.500">
                  ●
                </Text>
              </Box>
              <Box position="absolute" top="15%" right="15%" w="12px" h="12px">
                <Text fontSize="md" color="purple.500">
                  ●
                </Text>
              </Box>
              <Box position="absolute" top="30%" left="20%" w="10px" h="10px">
                <Text fontSize="sm" color="pink.500">
                  +
                </Text>
              </Box>
              <Box position="absolute" top="35%" right="25%" w="10px" h="10px">
                <Text fontSize="sm" color="orange.500">
                  ~
                </Text>
              </Box>
              <Box position="absolute" top="45%" left="15%" w="8px" h="8px">
                <Text fontSize="xs" color="purple.400">
                  ●
                </Text>
              </Box>
              <Box position="absolute" top="50%" right="20%" w="10px" h="10px">
                <Text fontSize="sm" color="red.500">
                  +
                </Text>
              </Box>
              <Box position="absolute" top="25%" left="80%" w="10px" h="10px">
                <Text fontSize="sm" color="orange.400">
                  ~
                </Text>
              </Box>

              <VStack spacing={6} w="100%" align="center">
                {/* Success Icon */}
                <Box position="relative" zIndex={2}>
                  <Image
                    src={successImage}
                    alt="Success"
                    w="120px"
                    h="120px"
                    objectFit="contain"
                  />
                </Box>

                {/* Success Message */}
                <VStack spacing={3} textAlign="center" zIndex={2}>
                  <Heading as="h2" size="lg" color="gray.900" fontWeight="bold">
                    Password Changed
                  </Heading>
                  <Text
                    color="gray.500"
                    fontSize="sm"
                    maxW="300px"
                    lineHeight="1.5"
                    textAlign="center"
                  >
                    Password changed successfully, you can login again with a
                    new password
                  </Text>
                </VStack>

                {/* Verify Account Button */}
                <Button
                  w="100%"
                  maxW="350px"
                  bg="#5A5BED"
                  height="52px"
                  color="white"
                  fontWeight="semibold"
                  borderRadius="100px"
                  fontSize="16px"
                  _hover={{ bg: '#4A4BDD' }}
                  onClick={handleVerifyAccount}
                  mt={6}
                  zIndex={2}
                  boxShadow="0 4px 12px rgba(90, 91, 237, 0.3)"
                >
                  Verify Account
                </Button>
              </VStack>
            </Box>
          </ModalContent>
        </Modal>
      ) : (
        // Desktop Success Modal - Centered Popup
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          size="lg"
          motionPreset="scale"
          closeOnOverlayClick={true}
          isCentered={true}
        >
          <ModalOverlay
            bg="rgba(0, 0, 0, 0.5)"
            backdropFilter="blur(12px)"
            sx={{
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
            }}
          />
          <ModalContent
            bg="white"
            borderRadius="20px"
            maxW="500px"
            w="90%"
            mx="auto"
            boxShadow="0 25px 50px rgba(0,0,0,0.25)"
            position="relative"
            minH="480px"
          >
            <Box px={10} py={10}>
              {/* Background decorative elements for desktop */}
              <Box position="absolute" top="12%" left="8%" w="14px" h="14px">
                <Text fontSize="md" color="red.500">
                  ●
                </Text>
              </Box>
              <Box position="absolute" top="18%" right="10%" w="12px" h="12px">
                <Text fontSize="sm" color="purple.500">
                  ●
                </Text>
              </Box>
              <Box position="absolute" top="30%" left="15%" w="10px" h="10px">
                <Text fontSize="xs" color="pink.500">
                  +
                </Text>
              </Box>
              <Box position="absolute" top="35%" right="18%" w="10px" h="10px">
                <Text fontSize="xs" color="orange.500">
                  ~
                </Text>
              </Box>
              <Box position="absolute" top="65%" left="12%" w="8px" h="8px">
                <Text fontSize="xs" color="purple.400">
                  ●
                </Text>
              </Box>
              <Box position="absolute" top="70%" right="15%" w="10px" h="10px">
                <Text fontSize="xs" color="red.500">
                  +
                </Text>
              </Box>
              <Box position="absolute" top="25%" right="25%" w="8px" h="8px">
                <Text fontSize="xs" color="orange.400">
                  ~
                </Text>
              </Box>

              <VStack spacing={7} w="100%" align="center">
                {/* Success Icon */}
                <Box position="relative" zIndex={2}>
                  <Image
                    src={successImage}
                    alt="Success"
                    w="130px"
                    h="130px"
                    objectFit="contain"
                  />
                </Box>

                {/* Success Message */}
                <VStack spacing={3} textAlign="center" zIndex={2}>
                  <Heading as="h2" size="lg" color="gray.900" fontWeight="bold">
                    Password Changed
                  </Heading>
                  <Text
                    color="gray.500"
                    fontSize="md"
                    maxW="350px"
                    lineHeight="1.5"
                    textAlign="center"
                  >
                    Password changed successfully, you can login again with a
                    new password
                  </Text>
                </VStack>

                {/* Verify Account Button */}
                <Button
                  w="100%"
                  maxW="350px"
                  bg="#5A5BED"
                  height="52px"
                  color="white"
                  fontWeight="semibold"
                  borderRadius="100px"
                  fontSize="16px"
                  _hover={{ bg: '#4A4BDD' }}
                  onClick={handleVerifyAccount}
                  mt={5}
                  zIndex={2}
                  boxShadow="0 4px 12px rgba(90, 91, 237, 0.3)"
                >
                  Verify Account
                </Button>
              </VStack>
            </Box>
          </ModalContent>
        </Modal>
      )}
    </>
  );
}
